import { useEffect, useRef, useState } from 'react';
import { usePresentationContext } from '../core/PresentationContext';
import styles from './WebContent.module.css';

interface WebContentProps {
  children: React.ReactNode;
  className?: string;
  animation?: {
    type: 'fade' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'zoom';
    delay?: number;
    duration?: number;
  };
}

// Animation timing constants
const FRAME_MS = 16; // Duration of one frame at 60fps
const INITIAL_CHECK_DELAY = 0;

const getSlideId = (deck: any) => {
  if (!deck) return 'unknown';
  const indices = deck.getIndices();
  return `${indices.h + 1}.${indices.v + 1}`;
};

export const WebContent = ({
  children,
  className = '',
  animation = { type: 'fade', delay: 0, duration: 500 },
}: WebContentProps) => {
  const { deck, isInitialized } = usePresentationContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [instanceId] = useState(() => Math.random().toString(36).slice(2, 7));

  const log = (message: string, data?: any, force?: boolean) => {
    if (force || (deck && contentRef.current)) {
      const currentSlide = deck?.getCurrentSlide();
      if (force || (currentSlide && currentSlide.contains(contentRef.current))) {
        const delay = animation.delay || 0;
        console.log(
          `[WebContent ${getSlideId(deck)}:${instanceId}] (delay: ${delay}ms) ${message}`,
          data
        );
      }
    }
  };

  useEffect(() => {
    if (!deck || !isInitialized) return;

    const resetAndShow = () => {
      // Step 1: Reset states
      setIsResetting(true);
      setShouldRender(false);

      // Step 2: Begin show sequence
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldRender(true);
          setTimeout(() => {
            setIsResetting(false);
            setIsVisible(true);
          }, FRAME_MS);
        });
      });
    };

    const handleSlideChange = (event: any) => {
      const currentSlide = event.currentSlide;
      if (!currentSlide || !contentRef.current) {
        log('No slide or ref', {}, true);
        setIsResetting(true);
        setIsVisible(false);
        return;
      }

      const isInSlide = currentSlide.contains(contentRef.current);
      if (isInSlide) {
        log('Entering slide');
        resetAndShow();
      } else {
        log('Leaving slide');
        setIsResetting(true);
        setIsVisible(false);
      }
    };

    deck.on('slidechanged', handleSlideChange);

    // Check initial slide with a delay to ensure ready state
    setTimeout(() => {
      const currentSlide = deck.getCurrentSlide();
      if (currentSlide && contentRef.current) {
        log('Initial slide check');
        handleSlideChange({ currentSlide });
      }
    }, INITIAL_CHECK_DELAY);

    return () => {
      log('Cleanup', {}, true);
      deck.off('slidechanged', handleSlideChange);
      setIsResetting(true);
      setIsVisible(false);
    };
    // We intentionally don't include isVisible in deps to avoid re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck, isInitialized]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={contentRef}
      className={`
        ${styles.container} 
        ${styles[`animation-${animation.type}`]}
        ${isVisible ? styles.visible : styles.hidden}
        ${isResetting ? styles.reset : ''}
        ${className}
      `}
      style={
        {
          '--animation-duration': `${animation.duration || 500}ms`,
          '--animation-delay': `${animation.delay || 0}ms`,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};
